import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../../lib/auth.context'
import { getUserProfile } from '../../lib/api/api'
import { ChangePasswordForm } from '../../components/auth/change-password-form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Separator } from '../../components/ui/separator'
import { UserResponse } from '../../lib/api/generated/models'
import { Skeleton } from '../../components/ui/skeleton'
import { getThemeClass } from '../../lib/theme'

export function AccountSettingsPage() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  console.log('AccountSettingsPage: Rendering account settings page')

  useEffect(() => {
    const loadProfile = async () => {
      console.log('AccountSettingsPage: Loading user profile')
      try {
        const data = await getUserProfile()
        console.log('AccountSettingsPage: User profile loaded successfully', data)
        setProfile(data)
      } catch (error) {
        console.error('AccountSettingsPage: Failed to load user profile:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [])

  return (
    <>
      <Helmet>
        <title>Account Settings</title>
      </Helmet>

      <div className="container">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>View and manage your profile information.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[300px]" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium">Email</h3>
                      <p className="text-muted-foreground">{profile?.email || user?.email}</p>
                    </div>
                    <div>
                      <h3 className="font-medium">Name</h3>
                      <p className="text-muted-foreground">
                        {profile ? `${profile.firstName} ${profile.lastName}` : 'Not available'}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-medium">Role</h3>
                      <p className="text-muted-foreground capitalize">
                        {profile?.userRole || 'Not available'}
                      </p>
                    </div>
                    {profile?.entityType && (
                      <div>
                        <h3 className="font-medium">Entity Type</h3>
                        <p className="text-muted-foreground capitalize">{profile.entityType}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Password</CardTitle>
                <CardDescription>Change your password to keep your account secure.</CardDescription>
              </CardHeader>
              <CardContent>
                <ChangePasswordForm />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>Add an extra layer of security to your account.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Two-factor authentication adds an extra layer of security to your account by requiring more than just a password to sign in.
                </p>
                {/* Two-factor authentication settings would go here */}
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication status: {profile?.totpStatus ? (
                    <span className="text-green-600 font-medium">Enabled</span>
                  ) : (
                    <span className="text-amber-600 font-medium">Not enabled</span>
                  )}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
